import type { IAddress, IRegionByDomain, IRoad } from '../types/geo';
import type { IGeoAddressDetailsSchema } from '../../node/repositories/countryside-search/entities/geo/GeoAddressDetailsSchema';
import type { IGeoForListPresenterSchema } from '../../node/repositories/countryside-search/entities/geo/GeoForListPresenterSchema';
import { isArrayWithItems } from './helpers';
import { repeatTask } from './repeat_task';
import { loadYmapsApi } from './ymaps';
import { RealtyRegions } from './regions_map';

export interface IGeoSuggestItem {
  displayName: string;
  type: string;
  value: string;
  hl: [number, number][];
}

export function isScriptError(item: {}): item is YMaps.IScriptError {
  return item && 'message' in item && (item as { message?: string }).message === 'scriptError';
}

export function addressSuggest(request: string, options?: YMaps.ISuggestOptions): Promise<YMaps.ISuggestResult[]> {
  return repeatTask(3, () =>
    loadYmapsApi({ require: ['suggest'] })
      .then(ymaps => ymaps.suggest(request, options))
      .then(items => {
        if (isScriptError(items)) {
          throw new Error((items as YMaps.IScriptError).message);
        }

        return items || [];
      }),
  );
}

export function getBoundedBy(location?: IRegionByDomain) {
  if (!location || !location.boundedBy || !location.boundedBy.lowerCorner || !location.boundedBy.upperCorner) {
    return undefined;
  }
  const { upperCorner, lowerCorner } = location.boundedBy;

  return [
    [upperCorner.lng, upperCorner.lat],
    [lowerCorner.lng, lowerCorner.lat],
  ] as [YMaps.TCoord, YMaps.TCoord];
}

export const getRoadFullName = (road: IRoad) => [road.directionName, road.name].filter(Boolean).join(' · ');

export const DEFAULT_CITY_REGIONS_IDS: { [key: number]: number } = {
  [RealtyRegions.Moscow]: RealtyRegions.MoscowArea,
  [RealtyRegions.StPetersburg]: RealtyRegions.StPetersburgArea,
};

export const normalizeNameForSearch = (name: string): string =>
  name
    .toLowerCase()
    .replace(/ё/g, 'е')
    .replace(/[^\wа-я]/g, '');

export const roadTypes = ['шоссе', 'трасса', 'дорога', 'автодорога'];

export const stationTypes = ['станция', 'платформа', 'вокзал', 'жд', 'ст', 'пл', 'вкз'];

export const ONLY_VILLAGES_REGEXP = /^\s*кп/i;

export const getFetchRoadParameters = (regionId: number = 1) => ({
  regionIds: [DEFAULT_CITY_REGIONS_IDS[regionId] || regionId],
});

export const getFetchStationParameters = (regionId: number = 1) => ({
  directionRegionIds: [DEFAULT_CITY_REGIONS_IDS[regionId] || regionId],
});

interface IGeoSuggest {
  displayName: string;
}

export const filterGeoSuggest = ({ displayName }: IGeoSuggest) =>
  !/^(Россия|метро|(железнодорожная |ж\.-д\. )?станция|.*шоссе[,\s])|коттеджный пос[её]лок|[a-z]/i.test(displayName);

export const getGeoRegionId = (geo?: IGeoForListPresenterSchema | null) =>
  (geo &&
    geo.address &&
    isArrayWithItems(geo.address.details) &&
    (geo.address.details as IGeoAddressDetailsSchema[])[0].id) ||
  undefined;

export const DC_REGIONS_MAP: { [key: number]: number } = {
  [RealtyRegions.MoscowArea]: RealtyRegions.Moscow,
  [RealtyRegions.StPetersburgArea]: RealtyRegions.StPetersburg,
};

export const getRegionInfoFromAddress = (rootAddress: IAddress | void) => {
  if (!rootAddress || !isArrayWithItems(rootAddress.details)) {
    return undefined;
  }
  const { fullName, id } = rootAddress.details[0];

  return {
    displayName: fullName,
    fullName,
    id,
    parentId: DC_REGIONS_MAP[id],
  };
};
